import { useState, useRef, useEffect } from 'react';
import { animate } from 'framer-motion';
import '../styles/About.css';
import LetterDrag from './LetterDrag';

const STICK_W = 36;
const STICK_H = 72;
const GAP = 6;

const StickFigure = ({ pose }) => {
    const s = { stroke: '#c9a84c', strokeWidth: '2.5', strokeLinecap: 'round' };
    return (
        <svg viewBox="0 0 40 80" width={STICK_W} height={STICK_H} fill="none" aria-hidden="true">
            <circle cx="20" cy="9" r="7.5" {...s} />
            <line x1="20" y1="17" x2="20" y2="44" {...s} />
            {pose === 'sitting' && <>
                <line x1="20" y1="27" x2="7" y2="38" {...s} />
                <line x1="20" y1="27" x2="33" y2="38" {...s} />
                <line x1="20" y1="44" x2="5" y2="55" {...s} />
                <line x1="20" y1="44" x2="35" y2="55" {...s} />
            </>}
            {pose === 'jumping' && <>
                <line x1="20" y1="27" x2="4" y2="17" {...s} />
                <line x1="20" y1="27" x2="36" y2="17" {...s} />
                <line x1="20" y1="44" x2="10" y2="35" {...s} />
                <line x1="20" y1="44" x2="30" y2="35" {...s} />
            </>}
        </svg>
    );
};

const StaircaseBtns = () => {
    const [pose, setPose] = useState('sitting');
    const [currentBtn, setCurrentBtn] = useState('resume');
    const btn1Ref = useRef(null);
    const btn2Ref = useRef(null);
    const containerRef = useRef(null);
    const stickRef = useRef(null);
    const busy = useRef(false);
    const [ready, setReady] = useState(false);

    const getPosAboveBtn = (btnRef) => {
        if (!btnRef.current || !containerRef.current) return { x: 0, y: 0 };
        const bR = btnRef.current.getBoundingClientRect();
        const cR = containerRef.current.getBoundingClientRect();
        return {
            x: bR.left - cR.left + bR.width / 2 - STICK_W / 2,
            y: bR.top - cR.top - STICK_H - GAP,
        };
    };

    useEffect(() => {
        if (!stickRef.current) return;
        const pos = getPosAboveBtn(btn1Ref);
        animate(stickRef.current, { x: pos.x, y: pos.y }, { duration: 0 });
        setReady(true);
    }, []);

    const handleHover = async (btn) => {
        if (btn === currentBtn || busy.current) return;
        busy.current = true;

        const fromRef = currentBtn === 'resume' ? btn1Ref : btn2Ref;
        const toRef = btn === 'resume' ? btn1Ref : btn2Ref;
        const from = getPosAboveBtn(fromRef);
        const to = getPosAboveBtn(toRef);
        const peakY = Math.min(from.y, to.y) - 70;

        setPose('jumping');
        await animate(stickRef.current, {
            x: [from.x, (from.x + to.x) / 2, to.x],
            y: [from.y, peakY, to.y],
        }, { duration: 0.55, ease: 'easeInOut', times: [0, 0.5, 1] });

        setPose('sitting');
        setCurrentBtn(btn);
        busy.current = false;
    };

    return (
        <div className="staircase-btns" ref={containerRef}>
            <div ref={stickRef} className="stick-abs" style={{ opacity: ready ? 1 : 0 }}>
                <StickFigure pose={pose} />
            </div>
            <a
                ref={btn1Ref}
                href="./images/My Resume.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="step-btn step-btn--1"
                onMouseEnter={() => handleHover('resume')}
            >
                my resume
            </a>
            <a
                ref={btn2Ref}
                href="#projects"
                className="step-btn step-btn--2"
                onMouseEnter={() => handleHover('projects')}
            >
                projects
            </a>
        </div>
    );
};

const About = () => {
    return (
        <div className="about" id="about">
            <div className="about-hero">
                <h1 className="about-heading">
                    <span className="heading-top">jonathan</span>
                    <span className="heading-bottom">okonkwo</span>
                </h1>
                <span className="hero-card hero-img-cover">
                    <img className="hero-img" src="./images/My Pic.png" alt="Jonathan Okonkwo" />
                </span>
                <span className="hero-card vinyl-cover">
                    <img className="vinyl-img" src="./images/vinyl.png" alt="Vinyl record" />
                </span>
                <span className="hero-card other-cover">
                    <i className="fa-solid fa-code other-icon"></i>
                </span>
            </div>

            <div className="about-body">
                <div className="about-text">
                    <p>
                        I'm a college junior pursuing a <b>Computer Science</b> degree with a passion for <b>full-stack software engineering</b>. I thrive on solving complex problems and enjoy building innovative solutions through hands-on projects.
                    </p>
                    <p>
                        With a <b>solid foundation</b> in <b>programming, data structures, and algorithms</b>, I'm eager to contribute to collaborative teams and continuously learn from real-world challenges.
                    </p>
                    <p>
                        I have a <b>passion for creating beautiful websites/apps</b>. It's really interesting to see my imaginations come to life, so I can share that part of me with the world.
                    </p>
                    <p className="letter-hint">For desktop users, you can rearrange the letters to get my name :)</p>
                    <LetterDrag />
                </div>
                <StaircaseBtns />
            </div>
        </div>
    );
};

export default About;
